import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FaWhatsapp, FaFacebookMessenger } from 'react-icons/fa';
import FilterChip from '../../components/ui/FilterChip';
import PageHeader from '../../components/ui/PageHeader';
import { ADMIN_PAGE_WIDTH, cx } from '../../components/ui/utils';
import { sendLeadEmail } from '../../services/messagingApi';

const API_BASE = 'https://us-central1-tarjeta-ad945.cloudfunctions.net';

const CHANNEL_LABELS = {
  whatsapp: { label: 'WhatsApp', className: 'bg-emerald-100 text-emerald-700', Icon: FaWhatsapp },
  messenger: { label: 'Messenger', className: 'bg-blue-100 text-blue-700', Icon: FaFacebookMessenger },
  email: { label: 'Email', className: 'bg-sky-100 text-sky-700', Icon: EnvelopeIcon },
};

const ChannelBadge = ({ channel }) => {
  const info = CHANNEL_LABELS[channel] || CHANNEL_LABELS.whatsapp;
  const Icon = info.Icon;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${info.className}`}>
      <Icon className="h-3 w-3" />
      {info.label}
    </span>
  );
};

// Un cliente existente nunca genera un lead (ver docs/journey-leads-y-seguimiento.md
// §9) — así que `clientId` y `leadId` son mutuamente excluyentes en la práctica.
const getContactType = (conv) => (conv.clientId ? 'cliente' : 'lead');

const TypeBadge = ({ conv }) => {
  const isClient = getContactType(conv) === 'cliente';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${
        isClient ? 'bg-violet-100 text-violet-700' : 'bg-orange-100 text-orange-700'
      }`}
    >
      {isClient ? 'Cliente' : 'Lead'}
    </span>
  );
};

const CONTACT_TYPE_FILTERS = [
  { id: 'todos', label: 'Todos' },
  { id: 'lead', label: 'Leads' },
  { id: 'cliente', label: 'Clientes' },
];

const formatTime = (timestamp) => {
  if (!timestamp?.toDate) return '';
  return timestamp.toDate().toLocaleString('es-PR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const replySubjectFor = (subject) => {
  const normalized = String(subject || '').trim();
  if (!normalized) return '';
  return /^re:\s*/i.test(normalized) ? normalized : `Re: ${normalized}`;
};

const AdminWhatsAppInbox = () => {
  const location = useLocation();
  // La burbuja flotante de mensajería (AdminMessagingLauncher) manda aquí
  // con este state cuando el cliente escribió por WhatsApp/Messenger/Email —
  // así abre directo esa conversación en vez de la lista general.
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(() => location.state?.conversationId || null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('todos');

  useEffect(() => {
    const q = query(collection(db, 'messaging_conversations'), orderBy('updatedAt', 'desc'), limit(100));
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      setConversations(list);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    const q = query(
      collection(db, 'messaging_conversations', selectedId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(200)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });
    return () => unsubscribe();
  }, [selectedId]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  const typeCounts = useMemo(() => {
    const counts = { lead: 0, cliente: 0, todos: conversations.length };
    conversations.forEach((conv) => {
      counts[getContactType(conv)] += 1;
    });
    return counts;
  }, [conversations]);

  const visibleConversations = useMemo(
    () =>
      typeFilter === 'todos'
        ? conversations
        : conversations.filter((conv) => getContactType(conv) === typeFilter),
    [conversations, typeFilter]
  );

  const isEmail = selectedConversation?.channel === 'email';

  useEffect(() => {
    setReply('');
    setPendingAttachments([]);
    setEmailSubject('');
    setError('');
  }, [selectedId]);

  useEffect(() => {
    if (!isEmail || !selectedId || emailSubject) return;
    const latestSubject = [...messages].reverse().find((msg) => msg.subject)?.subject;
    const nextSubject = replySubjectFor(latestSubject);
    if (nextSubject) setEmailSubject(nextSubject);
  }, [emailSubject, isEmail, messages, selectedId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;

    if (isEmail) {
      const profileId = selectedConversation.leadId || selectedConversation.clientId;
      if (!profileId || !emailSubject.trim()) return;

      setSending(true);
      setError('');
      try {
        await sendLeadEmail({ profileId, subject: emailSubject.trim(), message: reply.trim() });
        setReply('');
        setEmailSubject('');
      } catch (err) {
        console.error('❌ Error enviando email:', err);
        setError(err.message || 'No se pudo enviar el email. Intenta de nuevo.');
      } finally {
        setSending(false);
      }
      return;
    }

    const target = selectedConversation?.phone || selectedConversation?.messengerPsid;
    if (!target) return;

    setSending(true);
    setError('');
    try {
      const payload = selectedConversation.phone
        ? { phone: selectedConversation.phone, message: reply.trim() }
        : { psid: selectedConversation.messengerPsid, message: reply.trim() };
      const res = await fetch(`${API_BASE}/sendWhatsAppReply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, attachments: pendingAttachments }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Error al enviar');
      }
      setReply('');
      setPendingAttachments([]);
    } catch (err) {
      console.error('❌ Error enviando respuesta:', err);
      setError('No se pudo enviar el mensaje. Intenta de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const loadDraft = (message) => {
    setReply(String(message?.textBody || '').trim());
    setPendingAttachments(Array.isArray(message?.attachments) ? message.attachments.filter((item) => item?.url) : []);
    setError('');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className={cx('mx-auto flex h-screen flex-col px-4 py-6 sm:px-6 lg:px-8', ADMIN_PAGE_WIDTH)}>
        <PageHeader
          title="Centro de mensajería"
          description="WhatsApp y Messenger en un solo lugar — cada conversación queda ligada a su lead o cliente."
        />

        <div className="mt-5 flex min-h-0 flex-1 gap-5">
          {/* Lista de conversaciones */}
          <div className="flex w-96 shrink-0 flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <div className="flex flex-wrap gap-1.5">
                {CONTACT_TYPE_FILTERS.map((filter) => (
                  <FilterChip
                    key={filter.id}
                    label={filter.label}
                    count={typeCounts[filter.id]}
                    active={typeFilter === filter.id}
                    onClick={() => setTypeFilter(filter.id)}
                    className="px-2.5 py-1 text-xs"
                  />
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {visibleConversations.length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-400">
                  {conversations.length === 0
                    ? 'No hay conversaciones todavía.'
                    : 'Ningún contacto coincide con el filtro.'}
                </p>
              ) : (
                visibleConversations.map((conv) => (
                  <button
                    key={conv.id}
                    type="button"
                    onClick={() => setSelectedId(conv.id)}
                    className={cx(
                      'flex w-full flex-col items-start gap-1 border-b border-slate-100 px-4 py-3 text-left transition hover:bg-orange-50',
                      selectedId === conv.id && 'bg-orange-50'
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-800">
                        {conv.contactName || conv.phone || 'Contacto sin nombre'}
                      </span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <ChannelBadge channel={conv.channel} />
                        {conv.requiresFollowUp && (
                          <span className="h-2 w-2 rounded-full bg-amber-400" title="Sin responder" />
                        )}
                      </div>
                    </div>
                    <div className="flex w-full items-center gap-1.5">
                      <TypeBadge conv={conv} />
                      <span className="truncate text-xs text-slate-500">{conv.phone}</span>
                    </div>
                    <span className="truncate text-xs text-slate-400">{conv.lastMessagePreview}</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Hilo de mensajes */}
          <div className="flex flex-1 flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            {!selectedConversation ? (
              <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
                <div className="text-center">
                  <ChatBubbleLeftRightIcon className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                  Selecciona una conversación para ver el historial.
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800">
                        {selectedConversation.contactName || selectedConversation.phone || 'Contacto sin nombre'}
                      </p>
                      <ChannelBadge channel={selectedConversation.channel} />
                      <TypeBadge conv={selectedConversation} />
                    </div>
                    <p className="text-xs text-slate-500">{selectedConversation.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedConversation.leadId && (
                      <Link
                        to={`/admin/leads?lead=${encodeURIComponent(selectedConversation.leadId)}`}
                        className="rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                      >
                        Abrir lead
                      </Link>
                    )}
                    <span
                      className={cx(
                        'flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium',
                        selectedConversation.waitingOn === 'agent'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      )}
                    >
                      {selectedConversation.waitingOn === 'agent' ? (
                        <>
                          <ClockIcon className="h-3.5 w-3.5" /> Sin responder
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-3.5 w-3.5" /> Respondido
                        </>
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cx('flex', msg.direction === 'outbound' ? 'justify-end' : 'justify-start')}
                    >
                      <div
                        className={cx(
                          'max-w-[65%] rounded-2xl px-4 py-2 text-sm',
                          msg.direction === 'outbound'
                            ? 'bg-orange-500 text-white'
                            : msg.direction === 'draft'
                              ? 'border border-violet-200 bg-violet-50 text-slate-800'
                              : 'bg-slate-100 text-slate-800'
                        )}
                      >
                        {msg.direction === 'draft' && (
                          <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-wide text-violet-700">Borrador interno · no enviado</p>
                        )}
                        {msg.subject && <p className="mb-1 font-semibold">{msg.subject}</p>}
                        <p className="whitespace-pre-wrap">{msg.textBody}</p>
                        {Array.isArray(msg.attachments) && msg.attachments.map((attachment, index) => (
                          <a
                            key={`${attachment.url || attachment.name || index}`}
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 block overflow-hidden rounded-lg border border-violet-200 bg-white"
                          >
                            {String(attachment.contentType || '').startsWith('image/') ? (
                              <img src={attachment.url} alt={attachment.name || 'Adjunto'} className="max-h-48 w-full object-cover" />
                            ) : (
                              <span className="block px-3 py-2 text-xs font-semibold text-violet-700">{attachment.name || 'Ver adjunto'}</span>
                            )}
                          </a>
                        ))}
                        {msg.direction === 'draft' && (
                          <button
                            type="button"
                            onClick={() => loadDraft(msg)}
                            className="mt-2 rounded-lg bg-violet-600 px-2.5 py-1 text-xs font-bold text-white hover:bg-violet-700"
                          >
                            Usar este texto
                          </button>
                        )}
                        <p
                          className={cx(
                            'mt-1 text-[0.65rem]',
                            msg.direction === 'outbound' ? 'text-orange-100' : 'text-slate-400'
                          )}
                        >
                          {formatTime(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSend} className="flex flex-col gap-2 border-t border-slate-100 px-4 py-3">
                  {isEmail && (
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Asunto del email"
                      className="rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  )}
                  <div className="flex items-center gap-2">
                    {isEmail ? (
                      <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Escribe el mensaje..."
                        rows={3}
                        className="flex-1 resize-y rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    ) : (
                      <input
                        type="text"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Escribe una respuesta..."
                        className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    )}
                    <button
                      type="submit"
                      disabled={sending || !reply.trim() || (isEmail && !emailSubject.trim())}
                      className="flex items-center gap-1 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      {sending ? 'Enviando...' : 'Enviar'}
                    </button>
                  </div>
                  {pendingAttachments.length > 0 && !isEmail && (
                    <div className="flex flex-wrap gap-2">
                      {pendingAttachments.map((attachment, index) => (
                        <div key={`${attachment.url}-${index}`} className="relative overflow-hidden rounded-lg border border-blue-200 bg-blue-50 p-1">
                          <img src={attachment.url} alt={attachment.name || 'Imagen adjunta'} className="h-16 w-24 rounded object-cover" />
                          <button
                            type="button"
                            onClick={() => setPendingAttachments((items) => items.filter((_, itemIndex) => itemIndex !== index))}
                            className="absolute right-1 top-1 rounded-full bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold text-white"
                            title="Quitar imagen"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </form>
                {error && <p className="px-4 pb-2 text-xs text-red-500">{error}</p>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminWhatsAppInbox;
